import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  TennisBall,
  ArrowRight,
  ChartLineUp,
  UsersThree,
  Trophy,
  Microphone,
  ShieldCheck,
  Lightning,
  Confetti,
  Buildings,
  Star,
} from "@phosphor-icons/react";
import RequestDemoModal from "@/components/RequestDemoModal";

export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen auth-bg" data-testid="landing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-[#09090b]/85 backdrop-blur border-b border-[#27272a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
            <div className="w-7 h-7 rounded-md bg-[#dfff00] flex items-center justify-center font-heading text-black text-base">
              C
            </div>
            <div className="font-heading uppercase tracking-tight text-lg leading-none flex items-baseline">
              <span className="text-white">courtside</span><span className="text-[#dfff00] ml-0.5">OS</span>
            </div>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            <a href="#screens" className="btn-ghost text-xs hidden sm:inline-flex" data-testid="nav-screens">Product</a>
            <a href="#features" className="btn-ghost text-xs hidden sm:inline-flex" data-testid="nav-features">Features</a>
            <a href="#roles" className="btn-ghost text-xs hidden sm:inline-flex" data-testid="nav-roles">Who it's for</a>
            <button
              onClick={() => setDemoOpen(true)}
              className="btn-secondary text-xs"
              data-testid="nav-demo"
            >
              Request a demo
            </button>
            <Link to="/login" className="btn-primary text-xs" data-testid="nav-signin">
              Sign in <ArrowRight size={14} weight="bold" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16">
          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
            <div>
              <div className="font-label text-[#dfff00] mb-4" data-testid="hero-eyebrow">
                For UK & EU tennis clubs
              </div>
              <h1 className="font-heading uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="hero-title">
                Every junior's<br />
                progress —<br />
                <span className="text-[#dfff00]">in one place.</span>
              </h1>
              <p className="text-zinc-300 text-lg mt-6 max-w-xl leading-relaxed">
                courtsideOS gives coaches a courtside-fast way to log term evaluations,
                parents a live view of their child's growth, and head coaches a clear pulse
                on the whole club. LTA results sync automatically.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-8" data-testid="hero-ctas">
                <button
                  onClick={() => setDemoOpen(true)}
                  className="btn-primary"
                  data-testid="hero-demo-btn"
                >
                  Bring your club to courtsideOS <ArrowRight size={16} weight="bold" />
                </button>
                <Link to="/login" className="btn-secondary" data-testid="hero-signin-btn">
                  Sign in
                </Link>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-xs text-zinc-500">
                <span className="flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" className="text-[#16ec06]" /> Multi-tenant + GDPR-ready</span>
                <span className="flex items-center gap-1.5"><Lightning size={14} weight="fill" className="text-[#dfff00]" /> Works offline (PWA)</span>
                <span className="flex items-center gap-1.5"><Trophy size={14} weight="fill" className="text-[#dfff00]" /> Live LTA results</span>
              </div>
            </div>

            {/* Hero visual — desktop: layered cards. Mobile: simplified single card below CTAs. */}
            <div className="relative hidden lg:block" aria-hidden="true">
              {/* Ambient glow */}
              <div className="absolute -inset-10 bg-[#dfff00]/[0.06] blur-3xl rounded-full" />
              <div className="absolute top-1/3 -right-10 w-60 h-60 bg-[#16ec06]/[0.06] blur-3xl rounded-full" />

              {/* Card 1 — Trend chart (centerpiece, the "nice curves") */}
              <div className="relative surface p-5 shadow-2xl ring-1 ring-white/5" style={{ transform: "rotate(-1.5deg)" }} data-testid="hero-card-trend">
                <BrowserBar />
                <div className="flex items-baseline justify-between mb-1">
                  <div>
                    <div className="font-label">Sophie Wright · 11U · Parent view</div>
                    <div className="font-heading uppercase tracking-tight text-xl leading-tight">Progress, term over term</div>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-bold text-[#16ec06] bg-[#16ec06]/10 px-1.5 py-0.5 rounded">
                    <ChartLineUp size={10} weight="bold" /> +0.8
                  </div>
                </div>
                <HeroTrendChart />
                <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-2 text-[10px]">
                  <LegendDot color="#dfff00" label="Game Style" />
                  <LegendDot color="#16ec06" label="Mental" />
                  <LegendDot color="#60a5fa" label="Technical" />
                </div>
              </div>

              {/* Card 2 — Voice-to-text widget (the killer differentiator) */}
              <VoiceWidget />

              {/* Card 3 — LTA rank pill (bottom-left, the "result" card) */}
              <div className="absolute -bottom-8 -left-6 surface px-4 py-3 w-72 shadow-2xl ring-1 ring-[#dfff00]/20" style={{ transform: "rotate(-2deg)" }} data-testid="hero-card-rank">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Star size={11} weight="fill" className="text-[#dfff00]" />
                  <span className="font-label">11U Boys · Live from LTA</span>
                </div>
                <div className="flex items-end gap-4">
                  <div>
                    <div className="font-label text-[9px] text-zinc-500">Rank</div>
                    <div className="text-2xl font-heading text-[#dfff00] leading-none">#100</div>
                  </div>
                  <div>
                    <div className="font-label text-[9px] text-zinc-500">Points</div>
                    <div className="text-2xl font-heading text-white leading-none">413</div>
                  </div>
                  <div className="flex-1">
                    <div className="font-label text-[9px] text-zinc-500 mb-0.5">Recent form</div>
                    <div className="flex gap-0.5">
                      {["W", "L", "W", "W", "W"].map((r, i) => (
                        <span key={i} className={`px-1 py-0.5 rounded text-[9px] font-bold ${r === "W" ? "bg-[#16ec06]/15 text-[#16ec06]" : "bg-[#ff0026]/15 text-[#ff0026]"}`}>{r}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile-only compact hero visual — single trend card + LTA pill below */}
            <div className="lg:hidden mt-2" aria-hidden="true">
              <div className="surface p-4 shadow-2xl" data-testid="hero-mobile-trend">
                <BrowserBar />
                <div className="flex items-baseline justify-between mb-1">
                  <div>
                    <div className="font-label">Sophie · Parent view</div>
                    <div className="font-heading uppercase tracking-tight text-base leading-tight">Progress, term over term</div>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-bold text-[#16ec06] bg-[#16ec06]/10 px-1.5 py-0.5 rounded">
                    <ChartLineUp size={10} weight="bold" /> +0.8
                  </div>
                </div>
                <HeroTrendChart />
                <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-2 text-[10px]">
                  <LegendDot color="#dfff00" label="Game" />
                  <LegendDot color="#16ec06" label="Mental" />
                  <LegendDot color="#60a5fa" label="Technical" />
                </div>
              </div>
              <div className="surface px-4 py-3 mt-3 ring-1 ring-[#dfff00]/20" data-testid="hero-mobile-rank">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Star size={11} weight="fill" className="text-[#dfff00]" />
                  <span className="font-label">11U Boys · Live from LTA</span>
                </div>
                <div className="flex items-end gap-4 flex-wrap">
                  <div>
                    <div className="font-label text-[9px] text-zinc-500">Rank</div>
                    <div className="text-2xl font-heading text-[#dfff00] leading-none">#100</div>
                  </div>
                  <div>
                    <div className="font-label text-[9px] text-zinc-500">Points</div>
                    <div className="text-2xl font-heading text-white leading-none">413</div>
                  </div>
                  <div className="flex-1 min-w-[6rem]">
                    <div className="font-label text-[9px] text-zinc-500 mb-0.5">Recent form</div>
                    <div className="flex gap-0.5">
                      {["W", "L", "W", "W", "W"].map((r, i) => (
                        <span key={i} className={`px-1 py-0.5 rounded text-[9px] font-bold ${r === "W" ? "bg-[#16ec06]/15 text-[#16ec06]" : "bg-[#ff0026]/15 text-[#ff0026]"}`}>{r}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screens — real cropped screenshots */}
      <section id="screens" className="border-t border-[#27272a] bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="font-label text-[#dfff00] mb-3">See it in action</div>
          <h2 className="font-heading uppercase tracking-tight text-3xl sm:text-4xl mb-10 max-w-2xl">
            The whole club at a glance — from any screen.
          </h2>
          <div className="grid lg:grid-cols-12 gap-5" data-testid="screens-grid">
            <ScreenCard
              title="Coach dashboard"
              caption="Every assigned player, one tap to evaluate."
              src="/screens/coach.png"
              width={1280}
              height={700}
              span="lg:col-span-7"
              testid="screen-coach"
            />
            <ScreenCard
              title="Parent trend view"
              caption="Term-over-term curves — Δ badges show what's improving."
              src="/screens/trend.png"
              width={920}
              height={440}
              span="lg:col-span-5"
              testid="screen-trend"
            />
            <ScreenCard
              title="Live LTA results"
              caption="Counting tournaments, ranking points & recent form — straight from LTA."
              src="/screens/lta.png"
              width={976}
              height={1156}
              span="lg:col-span-12"
              testid="screen-lta"
              tall
            />
          </div>
        </div>
      </section>

      {/* Roles */}
      <section id="roles" className="border-t border-[#27272a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="font-label text-[#dfff00] mb-3">Who it's for</div>
          <h2 className="font-heading uppercase tracking-tight text-3xl sm:text-4xl mb-10">
            Built for every role on the court
          </h2>
          <div className="grid md:grid-cols-3 gap-4" data-testid="role-cards">
            <RoleCard
              icon={TennisBall}
              title="Coaches"
              testid="role-coaches"
              bullets={[
                "Log a full evaluation in under 60 seconds",
                "Voice-to-text comments — no typing on court",
                "Track every assigned player by term",
              ]}
            />
            <RoleCard
              icon={Buildings}
              title="Head Coaches"
              testid="role-head"
              bullets={[
                "Club-wide evaluation coverage at a glance",
                "Squads + one-click dispatch to coaches",
                "Audit, invites, and parent activation in-app",
              ]}
            />
            <RoleCard
              icon={UsersThree}
              title="Parents"
              testid="role-parents"
              bullets={[
                "See your child grow, term over term",
                "Coach comments + ratings in plain language",
                "Live LTA tournament results, no extra clicks",
              ]}
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-[#27272a] bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="font-label text-[#dfff00] mb-3">Why courtsideOS</div>
          <h2 className="font-heading uppercase tracking-tight text-3xl sm:text-4xl mb-10">
            Less admin. More coaching.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feature-cards">
            <Feature icon={ChartLineUp} title="Term evaluations" body="Score every junior across 18 competencies, term after term. Parents see the trend chart live." />
            <Feature icon={Trophy} title="LTA results sync" body="Pull official singles & doubles ranking points, recent form, and counting tournaments straight from the LTA." />
            <Feature icon={Microphone} title="Voice-to-text" body="Whisper-powered comment dictation — talk while you watch, the notes write themselves." />
            <Feature icon={UsersThree} title="Squads" body="Group players, dispatch them to a coach in one click, manage the whole roster from the club page." />
            <Feature icon={Lightning} title="Offline-ready PWA" body="Install courtsideOS to your phone home screen — works on court when wifi doesn't." />
            <Feature icon={ShieldCheck} title="Multi-tenant + GDPR" body="Every club is fully isolated. Parents, coaches, head coaches — each see only what they should." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#27272a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <Confetti size={36} weight="duotone" className="text-[#dfff00] mx-auto mb-4" />
          <h2 className="font-heading uppercase tracking-tight text-3xl sm:text-5xl mb-4">
            Bring your club <span className="text-[#dfff00]">on board</span>
          </h2>
          <p className="text-zinc-300 text-lg mb-8 max-w-2xl mx-auto">
            We onboard one club at a time — every head coach gets hands-on setup. Tell us about your club and we'll be in touch within 48 hours.
          </p>
          <button onClick={() => setDemoOpen(true)} className="btn-primary" data-testid="footer-demo-btn">
            Request a demo <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272a] py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-[#dfff00] flex items-center justify-center font-heading text-black text-[10px]">C</div>
            <span>courtsideOS · Every junior's progress, in one place.</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="hover:text-white" data-testid="footer-signin">Sign in</Link>
            <button onClick={() => setDemoOpen(true)} className="hover:text-white" data-testid="footer-contact">Contact</button>
          </div>
        </div>
      </footer>

      <RequestDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}

function BarRow({ label, value }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <div className="flex-1 text-zinc-300 truncate">{label}</div>
      <div className="flex gap-[2px]">
        {[1, 2, 3, 4, 5].map((n) => (
          <div
            key={n}
            className={`w-2 h-1 rounded-sm ${n <= value ? "bg-[#dfff00]" : "bg-[#27272a]"}`}
          />
        ))}
      </div>
    </div>
  );
}

function BrowserBar() {
  return (
    <div className="flex items-center gap-1.5 mb-3 -mt-1">
      <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
      <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
      <span className="w-2 h-2 rounded-full bg-[#28c840]" />
      <span className="ml-2 px-2 py-0.5 rounded bg-[#0a0a0a] border border-[#27272a] text-[9px] font-mono text-zinc-500 tracking-tight">
        courtsideos.app/players/sophie
      </span>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1 text-zinc-400">
      <span className="w-2.5 h-[3px] rounded-sm" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}

// Demo data — realistic upward progression across 4 UK school terms
const TREND_DATA = [
  { term: "Sum '25", Game: 2.6, Mental: 2.4, Technical: 2.9 },
  { term: "Aut '25", Game: 2.9, Mental: 2.7, Technical: 3.1 },
  { term: "Spr '26", Game: 3.4, Mental: 3.3, Technical: 3.5 },
  { term: "Sum '26", Game: 4.0, Mental: 3.8, Technical: 4.1 },
];

function HeroTrendChart() {
  return (
    <div style={{ width: "100%", height: 180, minHeight: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={TREND_DATA} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="term" tick={{ fontSize: 10, fill: "#71717a" }} stroke="#27272a" tickLine={false} axisLine={false} />
          <YAxis domain={[1, 5]} ticks={[1, 3, 5]} tick={{ fontSize: 10, fill: "#71717a" }} stroke="#27272a" tickLine={false} axisLine={false} width={24} />
          <Line type="monotone" dataKey="Game" stroke="#dfff00" strokeWidth={3} dot={{ r: 3.5, fill: "#dfff00", strokeWidth: 0 }} animationDuration={1400} animationBegin={200} />
          <Line type="monotone" dataKey="Mental" stroke="#16ec06" strokeWidth={3} dot={{ r: 3.5, fill: "#16ec06", strokeWidth: 0 }} animationDuration={1400} animationBegin={500} />
          <Line type="monotone" dataKey="Technical" stroke="#60a5fa" strokeWidth={3} dot={{ r: 3.5, fill: "#60a5fa", strokeWidth: 0 }} animationDuration={1400} animationBegin={800} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function RoleCard({ icon: Icon, title, bullets, testid }) {
  return (
    <div className="surface p-6 h-full" data-testid={testid}>
      <div className="w-10 h-10 rounded-md bg-[#dfff00]/15 flex items-center justify-center mb-4">
        <Icon size={20} weight="duotone" className="text-[#dfff00]" />
      </div>
      <h3 className="font-heading uppercase tracking-tight text-2xl mb-3">{title}</h3>
      <ul className="space-y-2">
        {bullets.map((b, i) => (
          <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
            <span className="text-[#dfff00] mt-1.5 w-1 h-1 rounded-full bg-[#dfff00] shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="surface p-5 h-full">
      <Icon size={22} weight="duotone" className="text-[#dfff00] mb-3" />
      <h3 className="font-heading uppercase tracking-tight text-lg mb-1.5">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function ScreenCard({ title, caption, src, span, testid, tall = false, width = 1280, height = 700 }) {
  return (
    <div className={`${span || ""} group`} data-testid={testid}>
      <div className="relative overflow-hidden rounded-lg border border-[#27272a] bg-[#09090b] shadow-2xl">
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[#27272a] bg-[#0d0d0d]">
          <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
          <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
          <span className="w-2 h-2 rounded-full bg-[#28c840]" />
          <span className="ml-2 text-[10px] font-mono text-zinc-500 truncate">courtsideos.app · {title.toLowerCase()}</span>
        </div>
        <div className={tall ? "max-h-[520px] overflow-hidden" : ""}>
          <img
            src={src}
            alt={title}
            width={width}
            height={height}
            className="w-full block transition-transform duration-500 group-hover:scale-[1.02]"
            loading="lazy"
          />
        </div>
      </div>
      <div className="mt-3">
        <div className="font-heading uppercase tracking-tight text-lg">{title}</div>
        <p className="text-sm text-zinc-400 mt-0.5">{caption}</p>
      </div>
    </div>
  );
}

// Voice-to-text widget — typing animation that loops, with a pulsing mic and waveform.
const VOICE_LINES = [
  "Strong week — committed to long rallies, recovery much improved.",
  "Serve placement sharper today. Confident on second serve.",
  "Needs to hold composure after losing the first set.",
];

function VoiceWidget() {
  const [lineIdx, setLineIdx] = React.useState(0);
  const [shown, setShown] = React.useState("");

  React.useEffect(() => {
    let mounted = true;
    let charIdx = 0;
    const line = VOICE_LINES[lineIdx];

    const tick = () => {
      if (!mounted) return;
      if (charIdx <= line.length) {
        setShown(line.slice(0, charIdx));
        charIdx += 1;
        setTimeout(tick, 35);
      } else {
        setTimeout(() => {
          if (mounted) setLineIdx((i) => (i + 1) % VOICE_LINES.length);
        }, 2200);
      }
    };
    tick();
    return () => { mounted = false; };
  }, [lineIdx]);

  return (
    <div
      className="absolute -top-6 -right-2 surface p-3.5 w-64 shadow-2xl ring-1 ring-white/5"
      style={{ transform: "rotate(3deg)" }}
      data-testid="hero-card-voice"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <div className="w-7 h-7 rounded-md bg-[#ff0026]/15 flex items-center justify-center">
            <Microphone size={14} weight="fill" className="text-[#ff0026]" />
          </div>
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#ff0026] animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-label leading-none">Coach · live dictation</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">Whisper · transcribing…</div>
        </div>
      </div>
      <div className="flex items-end gap-[3px] h-5 mb-2">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((i) => (
          <span
            key={i}
            className="w-[3px] bg-[#dfff00] rounded-sm"
            style={{
              animation: `wave 1.1s ease-in-out ${i * 0.08}s infinite`,
              transformOrigin: "bottom",
            }}
          />
        ))}
      </div>
      <p className="text-[11px] text-zinc-200 leading-snug min-h-[40px]">
        “{shown}<span className="inline-block w-[2px] h-3 bg-[#dfff00] align-middle ml-px animate-pulse" />”
      </p>
      <style>{`
        @keyframes wave {
          0%, 100% { transform: scaleY(0.3); opacity: 0.55; }
          50%      { transform: scaleY(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
}
